import React, { useState } from 'react';
import { FiUser, FiHome, FiShield, FiCreditCard, FiCheckCircle, FiAlertCircle, FiRefreshCw, FiLoader } from 'react-icons/fi';
import { ProfileProvider, useProfile } from '../context/ProfileContext';
import FranchiseProfile from '../components/profile/FranchiseProfile';
import OwnerDetails from '../components/profile/OwnerDetails';
import KYCModule from '../components/profile/KYCModule';
import BankAccounts from '../components/profile/BankAccounts';

// Loading Component
const ProfileLoading = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
    <div className="text-center">
      <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="text-muted">Loading profile data...</p>
    </div>
  </div>
);

// Error Component
const ProfileError = ({ error, onRetry }) => (
  <div className="alert alert-danger d-flex align-items-center justify-content-between">
    <div className="d-flex align-items-center">
      <FiAlertCircle className="me-2" size={20} />
      <div>
        <strong>Error loading profile</strong>
        <p className="mb-0 small">{error}</p>
      </div>
    </div>
    <button className="btn btn-outline-danger btn-sm" onClick={onRetry}>
      <FiRefreshCw className="me-1" />
      Retry
    </button>
  </div>
);

const ProfileContent = () => {
  const [activeTab, setActiveTab] = useState('franchise');
  const [refreshing, setRefreshing] = useState(false);
  const {
    calculateProfileCompletion,
    loading,
    error,
    refreshProfile,
    metadata,
    saving
  } = useProfile();

  const completion = calculateProfileCompletion();

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshProfile();
    setRefreshing(false);
  };

  const tabs = [
    {
      id: 'franchise',
      label: 'Franchise Profile',
      icon: <FiHome />,
      component: FranchiseProfile,
      completion: completion.sections.franchise
    },
    {
      id: 'owner',
      label: 'Owner Details',
      icon: <FiUser />,
      component: OwnerDetails,
      completion: completion.sections.owner
    },
    {
      id: 'kyc',
      label: 'KYC Documents',
      icon: <FiShield />,
      component: KYCModule,
      completion: completion.sections.kyc
    },
    {
      id: 'bank',
      label: 'Bank Accounts',
      icon: <FiCreditCard />,
      component: BankAccounts,
      completion: completion.sections.bank
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  // Show loading state on initial load
  if (loading && !refreshing) {
    return <ProfileLoading />;
  }

  return (
    <div className="container-fluid p-4">
      {/* Error Alert */}
      {error && <ProfileError error={error} onRetry={handleRefresh} />}

      {/* Profile Header */}
      <div className="glass-card p-4 mb-4">
        <div className="row align-items-center">
          <div className="col-md-8">
            <h2 className="fw-bold mb-3">Profile Management</h2>
            <p className="text-muted mb-3">
              Complete your profile to unlock all features and ensure smooth operations
            </p>

            {/* Overall Progress */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small text-muted">Profile Completion</span>
                <span className="fw-bold">{completion.overall}%</span>
              </div>
              <div className="progress" style={{ height: '10px' }}>
                <div
                  className="progress-bar bg-primary"
                  role="progressbar"
                  style={{ width: `${completion.overall}%` }}
                  aria-valuenow={completion.overall}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>

            {/* Completion Status */}
            {completion.overall === 100 ? (
              <div className="d-flex align-items-center text-success">
                <FiCheckCircle className="me-2" />
                <span className="fw-semibold">Profile Completed!</span>
              </div>
            ) : (
              <div className="d-flex align-items-center text-warning">
                <FiAlertCircle className="me-2" />
                <span>Complete your profile to access all features</span>
              </div>
            )}

            {/* Metadata Info */}
            {metadata?.lastUpdated && (
              <small className="text-muted d-block mt-2">
                Last updated: {new Date(metadata.lastUpdated).toLocaleDateString()}
              </small>
            )}
          </div>

          <div className="col-md-4 text-end">
            <div className="d-flex justify-content-end gap-3 mb-3">
              <div className="text-center">
                <div className="fs-2 fw-bold text-primary">{completion.overall}%</div>
                <small className="text-muted">Overall</small>
              </div>
              <div className="vr"></div>
              <div className="text-center">
                <div className="fs-2 fw-bold text-success">
                  {Object.values(completion.sections).filter(v => v === 100).length}
                </div>
                <small className="text-muted">Completed</small>
              </div>
            </div>

            {/* Refresh Button */}
            <button
              className="btn btn-outline-primary btn-sm rounded-pill px-3"
              onClick={handleRefresh}
              disabled={refreshing || saving}
            >
              {refreshing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Refreshing...
                </>
              ) : (
                <>
                  <FiRefreshCw className="me-2" />
                  Refresh
                </>
              )}
            </button>
          </div>
        </div>

        {/* Additional Metadata */}
        {metadata && (
          <div className="row mt-4 pt-3 border-top">
            <div className="col-6 col-md-3">
              <small className="text-muted d-block">Franchise ID</small>
              <span className="fw-semibold">{metadata.branchCode || 'N/A'}</span>
            </div>
            <div className="col-6 col-md-3">
              <small className="text-muted d-block">Account Status</small>
              <span className={`badge ${metadata.accountStatus === 'active' ? 'bg-success' : 'bg-warning'}`}>
                {metadata.accountStatus || 'Pending'}
              </span>
            </div>
            <div className="col-6 col-md-3">
              <small className="text-muted d-block">Subscription</small>
              <span className="fw-semibold text-capitalize">{metadata.subscriptionPlan || 'Basic'}</span>
            </div>
            <div className="col-6 col-md-3">
              <small className="text-muted d-block">Rating</small>
              <span className="fw-semibold">
                {metadata.franchiseRating ? `${metadata.franchiseRating} ⭐` : 'N/A'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Saving Indicator */}
      {saving && (
        <div className="alert alert-info d-flex align-items-center mb-3">
          <FiLoader className="spinner-border spinner-border-sm me-2" />
          Saving changes...
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="glass-card p-2 mb-4">
        <div className="row g-2">
          {tabs.map((tab) => (
            <div key={tab.id} className="col-md-3">
              <button
                className={`btn w-100 p-3 rounded-3 ${
                  activeTab === tab.id
                    ? 'btn-primary'
                    : 'btn-outline-secondary'
                }`}
                onClick={() => setActiveTab(tab.id)}
                disabled={loading}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fs-5">{tab.icon}</span>
                    <span className="fw-semibold">{tab.label}</span>
                  </div>
                  <div className="text-end">
                    <small className="d-block">{Math.round(tab.completion)}%</small>
                    <div
                      className="progress mt-1"
                      style={{ height: '3px', width: '40px' }}
                    >
                      <div
                        className={`progress-bar ${
                          tab.completion === 100 ? 'bg-success' : 'bg-warning'
                        }`}
                        style={{ width: `${tab.completion}%` }}
                      />
                    </div>
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {refreshing ? (
          <ProfileLoading />
        ) : (
          ActiveComponent && <ActiveComponent />
        )}
      </div>
    </div>
  );
};

const Profile = () => {
  return (
    <ProfileProvider>
      <ProfileContent />
    </ProfileProvider>
  );
};

export default Profile;